import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Button } from "react-bootstrap";
import { debounce } from "lodash";
import { showToast } from "main/shared/helpers";

const defaultToolbar = [
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link"],
  ["clean"],
];

const REQUIRED_TOKENS = ["[ROOM]", "[SAMPLE_TYPE]"];

const getHtmlTextLength = (html) => {
  if (!html) return 0;
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent?.trim().length || 0;
};

export default function ProtocolEditorForModal({
  text,
  onSave,
  maxLength = 20000,
  disabled = false,
  onCancle,
  toolbar = true,
}) {
  // Use text as initial value to ensure fresh state on each mount
  const initialText = text || "";
  const [editorHtml, setEditorHtml] = useState(initialText);
  const [length, setLength] = useState(getHtmlTextLength(initialText));
  const editorHtmlProtocolRef = useRef(initialText);
  const quillRef = useRef(null);

  // CRITICAL: Always sync ref with text prop on mount and updates
  useEffect(() => {
    editorHtmlProtocolRef.current = text || "";
    setEditorHtml(text || "");
    setLength(getHtmlTextLength(text));
  }, [text]);

  // Strip unwanted styles on paste - keep only allowed formatting
  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
        delta.ops.forEach((op) => {
          if (op.attributes) {
            // Keep only allowed attributes and flatten indents
            const newAttributes = {};

            if (op.attributes.bold) newAttributes.bold = op.attributes.bold;
            if (op.attributes.italic)
              newAttributes.italic = op.attributes.italic;
            if (op.attributes.underline)
              newAttributes.underline = op.attributes.underline;
            if (op.attributes.link) newAttributes.link = op.attributes.link;

            // Keep list type but remove indent
            if (op.attributes.list) {
              newAttributes.list = op.attributes.list;
            }

            op.attributes = newAttributes;
          }
        });
        return delta;
      });
    }
  }, []);

  const debouncedUpdate = useMemo(
    () =>
      debounce((content) => {
        editorHtmlProtocolRef.current = content;
        setLength(getHtmlTextLength(content));
      }, 200),
    []
  );

  useEffect(() => {
    return () => {
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate]);

  const handleChange = (content, _delta, source) => {
    if (source !== "user") {
      setEditorHtml(content);
      debouncedUpdate(content);
      return;
    }

    const newLength = getHtmlTextLength(content);
    if (newLength <= maxLength) {
      setEditorHtml(content);
      debouncedUpdate(content);
    } else {
      showToast("Character limit exceeded");
    }
  };

  const hasRequiredTokens = (html) =>
    REQUIRED_TOKENS.every((token) => html?.includes(token));

  const handleSave = () => {
    const currentValue = editorHtml || "";

    if (initialHasTokensRef.current && !hasRequiredTokens(currentValue)) {
      showToast("Protocol must include [ROOM] and [SAMPLE_TYPE] placeholders.");
      return;
    }
    onSave?.(currentValue);
  };

  const initialHasTokensRef = useRef(hasRequiredTokens(text || ""));

  useEffect(() => {
    initialHasTokensRef.current = hasRequiredTokens(text || "");
  }, [text]);

  return (
    <>
      <ReactQuill
        ref={quillRef}
        id="protocol-quill"
        theme="snow"
        value={editorHtml}
        readOnly={disabled}
        onChange={handleChange}
        modules={{
          toolbar: toolbar ? defaultToolbar : false,
        }}
      />
      <div className="rightsideactions">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            width: "100%",
          }}
        >
          <small>
            {length}/{maxLength}
          </small>
          <div className="d-flex mt-3">
            <Button
              className="text-secondary500 bg-transparent border-0"
              onClick={onCancle}
              disabled={disabled}
            >
              Cancel
            </Button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={disabled}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
